import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import '@/styles/globals.css';
import { clientConfig } from '@/client.config';
import { applyBrand } from '@/lib/theme';
import { MissingEnvError } from '@/lib/env';

applyBrand(clientConfig);

const root = createRoot(document.getElementById('root')!);

// The app module reads the environment at import time; a missing variable must produce a
// readable screen, not a blank page (edge case 0.a).
import('./App')
  .then(async ({ App }) => {
    // Outside production, expose the client so end-to-end tests can sign fixture users in
    // without the magic-link round trip (e2e/helpers.ts → loginAs).
    if (import.meta.env['VITE_APP_ENV'] !== 'production') {
      const { supabase } = await import('./lib/supabase');
      (window as unknown as { __supabase: unknown }).__supabase = supabase;
    }
    // the bug recorder watches from the first click; off with the feature (V13, 13.r)
    if (clientConfig.features.bugReports) {
      const { bugRecorder } = await import('./lib/bug-recorder-instance');
      bugRecorder.start();
    }
    // the last resort when the shell itself cannot be drawn: the crash screen with Reload (the
    // shell's own boundary, inside the providers, is the one that also offers Report this)
    const { CrashScreen, ErrorBoundary } = await import('./components/common/ErrorBoundary');
    root.render(
      <StrictMode>
        <ErrorBoundary fallback={() => <CrashScreen onReload={() => window.location.reload()} />}>
          <App />
        </ErrorBoundary>
      </StrictMode>,
    );
  })
  .catch((err: unknown) => {
    const message = err instanceof MissingEnvError ? err.message : 'The app failed to start.';
    root.render(
      <main className="mx-auto max-w-lg p-8 font-sans">
        <h1 className="text-lg font-semibold">Configuration needed</h1>
        <p className="mt-2 text-muted-foreground">{message}</p>
      </main>,
    );
    console.error(err);
  });
